import React, { useState } from 'react';

import { useIntl } from 'react-intl';

import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Select, Space } from 'antd';
import type { FormInstance } from 'antd/es/form';

import { clearOptionListsCache, useOptionLists } from '@src/hooks/useOptionLists';
import { useSelectFieldContext } from '@src/packages/eventTypes/context/SelectFieldContext';
import OptionListForm from '@src/packages/optionLists/components/OptionListForm';
import IntlMessages from '@src/util/IntlMessages';

interface OptionListPickerProps {
  name: (string | number)[];
  fieldKey: (string | number)[];
  label: string;
  form: FormInstance;
}

/** `null` creates a list; a string edits that list. */
type DrawerTarget = { listId: string | null } | null;

/**
 * Picks the saved list behind a selector field. It offers the lists the form may use (org-wide
 * ones plus those of the form's divisions); archived lists are hidden unless the field already
 * uses one, matching the backend rule. Admins and division editors create or edit lists here
 * without leaving the form.
 */
const OptionListPicker: React.FC<OptionListPickerProps> = ({ name, fieldKey, label, form }) => {
  const intl = useIntl();
  const { organizationId, divisionIds, onDirty, ownerDivisionId, canCreateList, canEditList } =
    useSelectFieldContext();
  const { lists, loading, error, refresh, service } = useOptionLists(organizationId, divisionIds);
  const [drawer, setDrawer] = useState<DrawerTarget>(null);
  const fieldPath = ['components', ...name];
  const currentId = Form.useWatch(fieldPath, form) as string | undefined;
  const currentList = lists.find(list => list.id === currentId);

  const options = lists
    .filter(list => !list.archived || list.id === currentId)
    .map(list => ({
      value: list.id,
      label: intl.formatMessage(
        { id: 'eventTypes.component.componentProps.optionListId.option' },
        { name: list.name, count: list.itemCount },
      ),
    }));

  const closeDrawer = (saved: boolean, savedListId?: string) => {
    setDrawer(null);
    if (!saved) return;
    clearOptionListsCache(organizationId);
    refresh();
    if (savedListId && savedListId !== currentId) {
      form.setFieldValue(fieldPath, savedListId);
      form.validateFields([fieldPath]).catch(() => undefined);
      onDirty?.();
    }
  };

  return (
    <>
      <Form.Item
        fieldKey={fieldKey}
        label={<IntlMessages id={label} />}
        name={name}
        rules={[{ required: true, message: <IntlMessages id="eventTypes.component.componentProps.optionListId.required" /> }]}
      >
        <Select
          loading={loading}
          options={options}
          showSearch
          optionFilterProp="label"
          placeholder={intl.formatMessage({ id: 'eventTypes.component.componentProps.optionListId.placeholder' })}
          notFoundContent={intl.formatMessage({
            id: error
              ? 'eventTypes.component.componentProps.optionListId.loadError'
              : 'eventTypes.component.componentProps.optionListId.empty',
          })}
          status={error ? 'error' : undefined}
        />
      </Form.Item>
      {(canCreateList || (currentList && canEditList?.(currentList))) && (
        <Space size={0}>
          {canCreateList && (
            <Button icon={<PlusOutlined />} onClick={() => setDrawer({ listId: null })} type="link">
              {intl.formatMessage({ id: 'eventTypes.component.componentProps.optionListId.create' })}
            </Button>
          )}
          {currentList && canEditList?.(currentList) && (
            <Button icon={<EditOutlined />} onClick={() => setDrawer({ listId: currentList.id })} type="link">
              {intl.formatMessage({ id: 'eventTypes.component.componentProps.optionListId.edit' })}
            </Button>
          )}
        </Space>
      )}
      {drawer && organizationId && (
        <OptionListForm
          // The form editor is already a drawer, and drawers are not nested.
          container="modal"
          divisionId={ownerDivisionId}
          listId={drawer.listId}
          onClose={closeDrawer}
          organizationId={organizationId}
          service={service}
        />
      )}
    </>
  );
};

export default OptionListPicker;
